import * as React from 'react';
import styled from '@emotion/styled';
import { useTranslation } from 'next-i18next';
import { Typography as MuiTypography, Button as MuiButton, CardContent } from '@mui/material';
import { useHighlightHandlers } from '../../contexts/HighlightContext/Highlight';
import { getText } from '../../utils/get-text';
import { useAppContext } from '../../contexts/AppContext';

export interface CardContentHProps {
  title?: string;
  description?: string;
  hideGameTitle?: boolean;
  hideDescription?: boolean;
  hidePlayButton?: boolean;
  limit?: number;
  descriptionLimit?: number;
  className?: string;
  hoverActive?: boolean;
  cardColor?: string;
}

const Button = React.forwardRef<HTMLButtonElement, any>((props, ref) =>
  useHighlightHandlers(MuiButton, { forwardedRef: ref, ...props })
);

const Typography = React.forwardRef<HTMLInputElement, any>((props, ref) =>
  useHighlightHandlers(MuiTypography, { forwardedRef: ref, ...props })
);

// Play button overlay that goes on top of the image
const PlayButtonContainer = styled('div')(({ theme }) => ({
  position: 'absolute',
  top: -12,
  left: '50%',
  transform: 'translateX(-50%)',
  right: 0,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  zIndex: 2,
  pointerEvents: 'none',
  maxWidth: 61,
  [theme.breakpoints.between('cmd', 'clg')]: {
    top: -8,
  },
}));

const PlayButton = styled(Button)(({ theme }) => ({
  minWidth: '110px',
  whiteSpace: 'nowrap',
  padding: '4px 24px',
  height: '26px',
  fontSize: '18px',
  textTransform: 'none',
  fontWeight: theme.typography.h1.fontWeight || 700,
  borderRadius: theme.theme_component_carousel_c18_card_button_border_radius || theme.spacing(1),
  pointerEvents: 'auto',
  boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.2)',
  background: theme.theme_component_carousel_c18_card_button_background || theme.palette.primary.main,
  color: theme.theme_component_carousel_c18_card_button_color || theme.palette.primary.contrastText,
  '&:hover': {
    background: theme.theme_component_carousel_c18_card_button_hover_background_color,
    color: theme.theme_component_carousel_c18_card_button_active_background_color,
  },
  [theme.breakpoints.between('cmd', 'clg')]: {
    fontSize: '6.37px',
    width: '61px',
    height: '13.79px',
    minWidth: 'auto',
  },
}));

// Content container (title and description)
const Container = styled(CardContent)(({ theme, hoverActive }: any) => ({
  display: 'flex',
  flex: 1,
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  position: 'relative',
  backgroundColor: theme.theme_component_card_background_color || '#FFFFFF',
  borderRadius: '0 0 8px 8px',
  '&:hover': hoverActive && {
    backgroundColor: theme.theme_component_card_hover_background_color || '#FFFFFF',
  },
  [theme.breakpoints.between('cmd', 'clg')]: {
    padding: '0 !important',
  },
}));

const TitleText = styled(Typography)(({ theme, hideDescription }: any) => ({
  width: '100%',
  fontWeight: theme.typography.h1.fontWeight || 700,
  marginTop: hideDescription ? theme.spacing(3) : theme.spacing(1),
  fontSize: '18px',
  lineHeight: 'normal',
  textAlign: 'center',
  color: theme.theme_component_title_color || '#000000',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  display: '-webkit-box',
  WebkitLineClamp: 1,
  WebkitBoxOrient: 'vertical',
  [theme.breakpoints.between('cmd', 'clg')]: {
    fontSize: '10px',
    lineHeight: '9.39px',
    margin: '15px 0',
  },
}));

const DescriptionText = styled(Typography)(({ theme, hideGameTitle }: any) => ({
  width: '100%',
  marginTop: hideGameTitle ? theme.spacing(2) : theme.spacing(1),
  fontWeight: 400, // theme.typography.h2.fontWeight || 400,
  fontSize: hideGameTitle ? '14px' : '12px',
  lineHeight: hideGameTitle ? '18px' : '14px',
  textAlign: 'center',
  color: theme.theme_component_game_description_font_color || '#666666',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  display: '-webkit-box',
  WebkitLineClamp: 1,
  WebkitBoxOrient: 'vertical',
  minHeight: '14px',
}));

const PlaceholderText = styled('div')(() => ({
  width: '80%',
  height: '100%',
  backgroundColor: 'transparent',
  opacity: 0,
}));

export const CardContentH = (props: CardContentHProps) => {
  const {
    title,
    description,
    hideGameTitle = false,
    hideDescription = false,
    hidePlayButton = false,
    hoverActive = true,
    limit,
    className,
  } = props;
  const { t } = useTranslation('card_content_f');
  const { useConfig } = useAppContext();
  const { componentOptions } = useConfig();

  return (
    <>
      <Container className={className} hoverActive={hoverActive}>
        {!hidePlayButton && (
          <PlayButtonContainer>
            <PlayButton variant="contained" disableElevation size="small">
              {componentOptions.component_card_title_text || t('play_now', 'Play')}
            </PlayButton>
          </PlayButtonContainer>
        )}
        {!hideGameTitle && (
          <TitleText variant="body1" hideDescription={hideDescription}>
            {title ? getText(title, limit || 35) : <PlaceholderText style={{ height: '14px' }} />}
          </TitleText>
        )}

        {!hideDescription && (
          <DescriptionText variant="body2" hideGameTitle={hideGameTitle}>
            {description ? getText(description, limit || 60) : <PlaceholderText style={{ height: '10px' }} />}
          </DescriptionText>
        )}
      </Container>
    </>
  );
};
